import {useAnalytics} from '@/hooks/useAnalytics';
import type {PhysicalAddress, ProductDeliveryChoice} from '@/lib/TypesOpenApi';
import {useCallback} from 'react';
import {proxy, subscribe} from 'valtio';
import {shippingAddress} from './shippingAddress';

export interface DeliveryOption {
  gfiberStoreName?: string;
  productDeliveryChoice: ProductDeliveryChoice;
  displayName?: string;
  address?: PhysicalAddress;
}

// if add any new field, add the new field to the below reset function also
export const deliveryOption = proxy<DeliveryOption>({
  productDeliveryChoice: 'PRODUCT_DELIVERY_CHOICE_UNSPECIFIED',
  displayName: '',
});

export const resetDeliveryOption = () => {
  deliveryOption.gfiberStoreName = undefined;
  deliveryOption.productDeliveryChoice = 'PRODUCT_DELIVERY_CHOICE_UNSPECIFIED';
  deliveryOption.displayName = '';
};

export type SetDeliveryOption = (
  deliveryType: ProductDeliveryChoice | string,
  gfiberStoreName?: string,
  displayName?: string,
  address?: PhysicalAddress,
) => void;

export const useSetDeliveryOption = (params?: {
  withoutAnalytics: boolean;
}): SetDeliveryOption => {
  const analytics = useAnalytics();

  return useCallback(
    (
      deliveryType: ProductDeliveryChoice | string,
      gfiberStoreName?: string,
      displayName?: string,
      address?: PhysicalAddress,
    ) => {
      if (!params?.withoutAnalytics) {
        analytics?.trackSikShippingCardSelection(
          deliveryOption.productDeliveryChoice,
          deliveryType,
        );
      }

      if (!deliveryType) {
        return;
      }

      deliveryOption.gfiberStoreName = gfiberStoreName;
      deliveryOption.productDeliveryChoice = isDeliveryChoice(deliveryType)
        ? deliveryType
        : 'PRODUCT_DELIVERY_CHOICE_UNSPECIFIED';
      deliveryOption.displayName = displayName;
      deliveryOption.address = address;
    },
    [analytics, params?.withoutAnalytics],
  );
};

export const updateDeliveryAddress = (address?: PhysicalAddress) => {
  deliveryOption.address = address;
};

// There are differing delivery choice enum values that have to
// boil down to ProductDeliveryChoice to be used in userSelection.
const validChoices = [
  'PRODUCT_DELIVERY_CHOICE_UNSPECIFIED',
  'PRO_INSTALL',
  'PICKUP',
  'SHIPPING',
  'BYOR',
];

function isDeliveryChoice(
  choice: ProductDeliveryChoice | string,
): choice is ProductDeliveryChoice {
  return validChoices.includes(choice);
}

subscribe(shippingAddress, () => {
  if (
    deliveryOption.productDeliveryChoice === 'SHIPPING' &&
    shippingAddress.shippingAddressInfo?.physicalAddress
  ) {
    updateDeliveryAddress(shippingAddress.shippingAddressInfo?.physicalAddress);
  }
});
